import React, { useContext, useEffect, useState } from 'react'
import { NavLink, useHistory, useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { Menu, MenuItem } from '@mui/material'
import { useAppSelector } from 'store/hooks'
import { NotificationsServiceContext } from 'settings/context'
import { AppServices } from 'middleware'
import { LucideIcon } from 'utils/lucideIcon'
import { Chevron } from 'uiComponents/chevron'
import Feature from 'features/feature'
import { AccountSelector } from 'header/accountSelector'
import { GlobalSearchTrigger } from 'globalSearch/globalSearchTrigger'
import { showErrorMessage } from 'uiComponents/message'
import i18n, { Language, LANGUAGES, switchLanguage, useTranslation } from 'translations'
import './topBar.scss'

interface TopBarProps {
    accountSlug: string | null
}

export function TopBar({ accountSlug }: TopBarProps) {
    const profile = useAppSelector((state) => state.profile.profile)
    const selfSignUp = useAppSelector((state) => state.profile?.profile?.signUp || null)
    const isReseller = useAppSelector((state) => !!state.auth.user?.resellerId)
    const notificationsService = useContext(NotificationsServiceContext)
    const [newNotificationsCount, setNewNotificationsCount] = useState<number>(0)
    const location = useLocation()

    useEffect(() => {
        if (!accountSlug || isReseller) {
            return
        }
        notificationsService.getNewNotificationsCount(accountSlug).then((count) => {
            setNewNotificationsCount(count)
        })
    }, [accountSlug, isReseller])

    useEffect(() => {
        if (/\/feed\b/.test(location.pathname)) {
            setNewNotificationsCount(0)
        }
    }, [location.pathname])

    // Our own language selector (BUIL-819): lists each language in its own
    // name and reloads through switchLanguage, because many class components
    // call t() at import time and would otherwise show mixed languages.
    const { t } = useTranslation()
    const [languageMenuAnchor, setLanguageMenuAnchor] = useState<HTMLElement | null>(null)
    const [switchingTo, setSwitchingTo] = useState<Language | null>(null)
    const currentLanguage = LANGUAGES.find((language) => language.code === i18n.resolvedLanguage) ?? LANGUAGES[0]
    const chooseLanguage = async (language: Language) => {
        if (language === currentLanguage.code) {
            setLanguageMenuAnchor(null)
            return
        }
        setSwitchingTo(language)
        try {
            // Reloads the page once the language is ready.
            await switchLanguage(language)
        } catch {
            showErrorMessage(t('sidebar.language.loadError'))
        } finally {
            setSwitchingTo(null)
            setLanguageMenuAnchor(null)
        }
    }

    const logClick = (path: string) => {
        AppServices.loggingService.logAction('navigation', {
            click_to: path,
            source: 'top_bar',
        })
    }

    // Profile menu: avatar + name as the button, Profile and Logout inside.
    const history = useHistory()
    const [profileMenuAnchor, setProfileMenuAnchor] = useState<HTMLElement | null>(null)
    const [helpMenuAnchor, setHelpMenuAnchor] = useState<HTMLElement | null>(null)
    const firstName = profile?.firstName?.trim() || ''
    const lastName = profile?.lastName?.trim() || ''
    const displayName = firstName ? `${firstName}${lastName ? ` ${lastName[0]}.` : ''}` : profile?.email || 'Profile'
    const initials = firstName
        ? `${firstName[0]}${lastName[0] || ''}`.toUpperCase()
        : (profile?.email || 'P')[0].toUpperCase()
    const openProfilePage = () => {
        setProfileMenuAnchor(null)
        logClick('/personal_settings/personal')
        history.push('/personal_settings/personal')
    }
    const logout = () => {
        setProfileMenuAnchor(null)
        AppServices.loginService.logout()
    }

    return (
        <div className="top-bar" id="top-bar">
            <div className="top-bar-account">
                <AccountSelector selfSignUp={selfSignUp} />
            </div>
            {accountSlug && (
                <Feature name="globalSearch" accountSlug={accountSlug}>
                    <div className="top-bar-search">
                        <GlobalSearchTrigger />
                    </div>
                </Feature>
            )}
            <div className="top-bar-spacer" />
            <button
                type="button"
                className="top-bar-translate"
                aria-haspopup="menu"
                aria-expanded={!!languageMenuAnchor}
                aria-label={t('sidebar.language.triggerLabel', { language: currentLanguage.name })}
                onClick={(event) => setLanguageMenuAnchor(event.currentTarget)}
            >
                <LucideIcon iconName="Globe" size={18} />
                <span className="top-bar-translate-label">{currentLanguage.name}</span>
            </button>
            <Menu
                className="top-bar-language-menu"
                anchorEl={languageMenuAnchor}
                open={!!languageMenuAnchor}
                onClose={() => setLanguageMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {LANGUAGES.map((language) => (
                    <MenuItem
                        key={language.code}
                        lang={language.code}
                        disabled={!!switchingTo}
                        selected={language.code === currentLanguage.code}
                        onClick={() => chooseLanguage(language.code)}
                    >
                        {language.name}
                        {language.code === currentLanguage.code && <LucideIcon iconName="Check" size={16} />}
                        {language.code === switchingTo && (
                            <LucideIcon iconName="LoaderCircle" size={16} className="spinning" />
                        )}
                    </MenuItem>
                ))}
            </Menu>
            <button
                type="button"
                className="top-bar-help"
                aria-haspopup="menu"
                aria-expanded={!!helpMenuAnchor}
                onClick={(event) => setHelpMenuAnchor(event.currentTarget)}
            >
                <LucideIcon iconName="CircleHelp" size={18} />
                <span className="top-bar-help-label">{t('header.topBar.help')}</span>
                <Chevron direction={helpMenuAnchor ? 'up' : 'down'} size={12} strokeWidth={1.5} />
            </button>
            <Menu
                className="top-bar-profile-menu"
                anchorEl={helpMenuAnchor}
                open={!!helpMenuAnchor}
                onClose={() => setHelpMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {[
                    {
                        label: t('header.topBar.knowledgeBase'),
                        href: 'https://support.convious.com',
                        icon: 'BookOpen' as const,
                    },
                    {
                        label: t('header.topBar.support'),
                        href: 'https://convious.com/support',
                        icon: 'LifeBuoy' as const,
                    },
                ].map(({ label, href, icon }) => (
                    <MenuItem
                        key={href}
                        component="a"
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => {
                            setHelpMenuAnchor(null)
                            logClick(href)
                        }}
                    >
                        <LucideIcon iconName={icon} size={16} />
                        {label}
                    </MenuItem>
                ))}
            </Menu>
            {!isReseller && accountSlug && (
                <NavLink
                    to={`/account/${accountSlug}/notifications/feed`}
                    className={classNames('top-bar-action', { bubble: newNotificationsCount > 0 })}
                    aria-label="Notifications"
                    title="Notifications"
                    onClick={() => logClick(`/account/${accountSlug}/notifications/feed`)}
                >
                    <LucideIcon iconName="Bell" size={18} />
                </NavLink>
            )}
            <div className="top-bar-divider" />
            <button
                type="button"
                className={classNames('top-bar-profile', { bubble: profile && !profile.emailConfirmed })}
                aria-haspopup="menu"
                aria-expanded={!!profileMenuAnchor}
                onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
            >
                <span className="top-bar-profile-avatar">
                    {profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : initials}
                </span>
                <span className="top-bar-profile-name notranslate">{displayName}</span>
                <Chevron direction={profileMenuAnchor ? 'up' : 'down'} size={12} strokeWidth={1.5} />
            </button>
            <Menu
                className="top-bar-profile-menu"
                anchorEl={profileMenuAnchor}
                open={!!profileMenuAnchor}
                onClose={() => setProfileMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={openProfilePage}>
                    <LucideIcon iconName="UserPen" size={16} />
                    Profile
                </MenuItem>
                <MenuItem onClick={logout}>
                    <LucideIcon iconName="LogOut" size={16} />
                    Logout
                </MenuItem>
            </Menu>
        </div>
    )
}

export default TopBar
